import { useContext } from 'react'

import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import PropTypes from 'prop-types'

import {
  faBan, faCheckCircle,
} from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams, sortArrayBy } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { generateUrls } from '@hooks/useProvider'

import PageContext from '@contexts/pageContext'

import useProviders from '@hooks/useProviders'
import useSelector from '@hooks/useSelector'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import ProviderListItem from '../ProviderListItem'

const buildPageFilterFields = (entities) => {
  const { services } = entities

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      label: 'Service',
      values: sortArrayBy(Object.values(services), 'asc', 'title').map(service => ({
        key: service.id,
        label: service.title,
      })),
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        endpoint: 'service_products',
        entityKey: 'serviceProducts',
      },
      key: 'service_product_id',
    },
  ]
}

const PageContent = (props) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const entities = useSelector(reduxState => reduxState.entities)

  const { active } = parsePermittedQueryParams(getQueryParamsFromHash(), ['active'])
  const isActive = active || 'true'

  const providersPayload = useProviders({
    filters: { active: isActive, ...pageFilters },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviders,
    filteredProvidersCount,
    hasFilteredProviders,
    loading,
  } = providersPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateProviderModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { providersIndexUrl, inactiveProvidersIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Providers | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateProviderModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey={isActive}
        tabBarItems={[
          {
            href: providersIndexUrl,
            icon: faCheckCircle,
            key: 'true',
            title: 'Active',
          },
          {
            href: inactiveProvidersIndexUrl,
            icon: faBan,
            key: 'false',
            title: 'Inactive',
          },
        ]}
        title="Providers"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredProviders && <ListBlankState />}

            {!loading && hasFilteredProviders && (
              <DashboardModule title="Providers">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredProviders.map(provider => (
                      <ProviderListItem key={provider.id} provider={provider} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredProvidersCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields(entities)}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

PageContent.propTypes = {
  pageFilters: PropTypes.object.isRequired,
}

export default PageContent
